import React, { useState, useEffect } from "react";
import { 
  Button, 
  Table, 
  Modal, 
  Form, 
  Input, 
  InputNumber, 
  Spin, 
  Alert, 
  Card, 
  Row,
  Col,
  Space,
  Popconfirm,
  Typography,
  Select,
  DatePicker,
  Statistic
} from "antd";
import { 
  PlusOutlined, 
  MinusOutlined, 
  SaveOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  InfoCircleOutlined,
  BarcodeOutlined,
  FileExcelOutlined,
  FilePdfOutlined
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import Papa from 'papaparse';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const API_BASE_URL = "https://agpipefitting.org.et/api";
const CURRENCY_FORMAT = { style: 'currency', currency: 'ETB' };
const DATE_FORMAT = "YYYY-MM-DD";

const initialFormState = {
  re_note: "",
  date: moment(),
  received_from: "",
  items: [{ 
    sn: 1, 
    description: "", 
    order_no: "", 
    um: "", 
    qty: "", 
    unit_price: "",
    remark: "" 
  }]
};

const FinishedGoodsReceivingNote = () => {
  const [form] = Form.useForm();
  const [currentEntry, setCurrentEntry] = useState(initialFormState);
  const [entries, setEntries] = useState([]);
  const [visible, setVisible] = useState(false);
  const [expandedRowKey, setExpandedRowKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportParams, setReportParams] = useState({
    type: 'monthly',
    year: moment().year(),
    quarter: Math.ceil((moment().month() + 1) / 3),
    month: moment().month() + 1,
    custom_range: [moment().startOf('month'), moment().endOf('month')],
    selected_week: moment(),
    reference_number: null
  });

  useEffect(() => {
    fetchEntries();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchEntries = async () => { 
    try {
      const { data } = await axios.get(`${API_BASE_URL}/finished-goods`);
      setEntries(data.data || []);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch entries");
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        date: values.date.format(DATE_FORMAT),
        items: values.items.map(item => ({
          ...item,
          sn: Number(item.sn),
          qty: Number(item.qty),
          unit_price: Number(item.unit_price)
        }))
      };

      const url = isEditing 
        ? `${API_BASE_URL}/finished-goods/${currentEntry.id}`
        : `${API_BASE_URL}/finished-goods`;

      const method = isEditing ? "put" : "post";
      
      const { data } = await axios[method](url, payload);
      
      setEntries(isEditing 
        ? entries.map(entry => entry.id === data.data.id ? data.data : entry)
        : [...entries, data.data]);
      
      handleModalClose();
      setSuccessMessage(isEditing ? "Entry updated successfully" : "New entry created successfully");
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || "Error processing request");
    }
  };

  const handleEdit = (entry) => {
    const entryWithMoment = {
      ...entry,
      date: moment(entry.date, DATE_FORMAT),
      items: entry.items.map(item => ({
        ...item,
        qty: Number(item.qty),
        unit_price: Number(item.unit_price)
      }))
    };
    form.setFieldsValue(entryWithMoment);
    setCurrentEntry(entryWithMoment);
    setIsEditing(true);
    setVisible(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/finished-goods/${id}`);
      setEntries(entries.filter(entry => entry.id !== id));
      setSuccessMessage("Entry deleted successfully");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete entry");
    }
  };

  const handleModalClose = () => {
    form.resetFields();
    setVisible(false);
    setIsEditing(false);
    setCurrentEntry(initialFormState);
  };

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    try {
      let params = { re_note: reportParams.reference_number };
      
      switch(reportParams.type) {
        case 'yearly':
          params = { 
            ...params,
            start_date: moment().year(reportParams.year).startOf('year').format(DATE_FORMAT),
            end_date: moment().year(reportParams.year).endOf('year').format(DATE_FORMAT)
          };
          break;
        case 'quarterly':
          const quarterStart = (reportParams.quarter - 1) * 3;
          params = {
            ...params,
            start_date: moment().year(reportParams.year).month(quarterStart).startOf('month').format(DATE_FORMAT),
            end_date: moment().year(reportParams.year).month(quarterStart + 2).endOf('month').format(DATE_FORMAT)
          };
          break;
        case 'monthly':
          params = {
            ...params,
            start_date: moment().year(reportParams.year).month(reportParams.month - 1).startOf('month').format(DATE_FORMAT),
            end_date: moment().year(reportParams.year).month(reportParams.month - 1).endOf('month').format(DATE_FORMAT)
          };
          break;
        case 'weekly':
          params = {
            ...params,
            start_date: reportParams.selected_week.startOf('week').format(DATE_FORMAT),
            end_date: reportParams.selected_week.endOf('week').format(DATE_FORMAT)
          };
          break;
        case 'custom':
          params = {
            ...params,
            start_date: reportParams.custom_range[0].format(DATE_FORMAT),
            end_date: reportParams.custom_range[1].format(DATE_FORMAT)
          };
          break;
      }

      const { data } = await axios.get(`${API_BASE_URL}/finished-goods/report`, { params });
      setReportData(data.data);
      setSuccessMessage("Report generated successfully");
    } catch (err) {
      setError("Failed to generate report");
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleExportCSV = () => {
    try {
      const csvData = reportData.map(entry => ({
        'Reference No': entry.re_note,
        Date: moment(entry.date).format(DATE_FORMAT),
        'Received From': entry.received_from,
        'Total Items': entry.items.length,
        'Total Value': entry.items.reduce((sum, item) => sum + (item.qty * item.unit_price), 0)
          .toLocaleString('en-US', CURRENCY_FORMAT)
      }));

      const csv = Papa.unparse(csvData);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `goods-report-${moment().format('YYYYMMDD-HHmmss')}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setSuccessMessage("CSV exported successfully");
    } catch (err) {
      setError("Failed to generate CSV export");
    }
  };

  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();
      const title = 'Finished Goods Receiving Report';
      const headers = [['Reference No', 'Date', 'Received From', 'Items Count', 'Total Value']];
      const data = reportData.map(entry => [
        entry.re_note,
        moment(entry.date).format(DATE_FORMAT),
        entry.received_from,
        entry.items.length,
        entry.items.reduce((sum, item) => sum + (item.qty * item.unit_price), 0)
          .toLocaleString('en-US', CURRENCY_FORMAT)
      ]);

      doc.setFontSize(16);
      doc.text(title, 14, 15);
      doc.autoTable({
        startY: 25,
        head: headers,
        body: data,
        theme: 'grid',
        styles: { fontSize: 10 },
        headStyles: { fillColor: [41, 128, 185], textColor: 255 }
      });

      doc.save(`goods-report-${moment().format('YYYYMMDD-HHmmss')}.pdf`);
      setSuccessMessage("PDF exported successfully");
    } catch (err) {
      setError("Failed to generate PDF export");
    }
  };

  const columns = [
    {
      title: 'Ref.No',
      dataIndex: 're_note',
      width: 120,
      sorter: (a, b) => a.re_note.localeCompare(b.re_note)
    },
    {
      title: 'Date',
      dataIndex: 'date',
      render: date => moment(date).format("DD MMM YYYY"),
      width: 120,
      sorter: (a, b) => moment(a.date).unix() - moment(b.date).unix()
    },
    {
      title: 'Received From',
      dataIndex: 'received_from',
      ellipsis: true
    },
    {
      title: 'Items',
      dataIndex: 'items',
      render: items => items.length,
      width: 100,
      sorter: (a, b) => a.items.length - b.items.length
    },
    {
      title: 'Total Value',
      dataIndex: 'items',
      render: items => items.reduce((sum, item) => sum + (item.qty * item.unit_price), 0)
        .toLocaleString('en-US', CURRENCY_FORMAT),
      width: 150,
      sorter: (a, b) => 
        a.items.reduce((sum, item) => sum + (item.qty * item.unit_price), 0) -
        b.items.reduce((sum, item) => sum + (item.qty * item.unit_price), 0)
    },
    {
      title: 'Actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button 
            icon={<InfoCircleOutlined />} 
            onClick={() => setExpandedRowKey(expandedRowKey === record.id ? null : record.id)}
          >
            Details
          </Button>
          <Button 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Delete this entry?"
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            cancelText="Cancel"
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const expandable = {
    expandedRowRender: record => (
      <Table
        columns={[
          { title: 'SN', dataIndex: 'sn', width: 80 },
          { title: 'Description', dataIndex: 'description', ellipsis: true },
          { title: 'Order No', dataIndex: 'order_no', width: 120 },
          { title: 'UM', dataIndex: 'um', width: 100 },
          { 
            title: 'QTY', 
            dataIndex: 'qty',
            render: (text) => Number(text).toLocaleString()
          },
          { 
            title: 'Unit Price', 
            dataIndex: 'unit_price',
            render: (text) => Number(text).toLocaleString('en-US', CURRENCY_FORMAT)
          },
          { 
            title: 'Total', 
            render: (_, item) => (item.qty * item.unit_price).toLocaleString('en-US', CURRENCY_FORMAT)
          },
          { title: 'Remark', dataIndex: 'remark', ellipsis: true },
        ]}
        dataSource={record.items}
        pagination={false}
        bordered
        size="small"
      />
    ),
    expandRowByClick: true,
    expandedRowKeys: [expandedRowKey],
    onExpand: (expanded, record) => setExpandedRowKey(expanded ? record.id : null)
  };

  const reportColumns = [
    { title: 'Ref.No', dataIndex: 're_note' },
    { title: 'Date', dataIndex: 'date', render: date => moment(date).format('DD MMM YYYY') },
    { title: 'Received From', dataIndex: 'received_from' },
    { title: 'Items Count', dataIndex: 'items', render: items => items.length },
    { 
      title: 'Total Value', 
      render: (_, record) => record.items.reduce((sum, item) => sum + (item.qty * item.unit_price), 0)
        .toLocaleString('en-US', CURRENCY_FORMAT)
    }
  ];

  const referenceNumbers = [...new Set(entries.map(entry => entry.re_note))];

  return (
    <div className="p-4">
      <Card className="mb-4 shadow-sm">
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={4} className="mb-0">
              <BarcodeOutlined /> Finished Goods Receiving Notes
            </Title>
          </Col>
          <Col>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => setVisible(true)}
            >
              New Entry
            </Button>
          </Col>
        </Row>
      </Card>

      {error && <Alert message={error} type="error" showIcon closable />}
      {successMessage && <Alert message={successMessage} type="success" showIcon closable />}

      <Card title="Analytics & Reporting" className="mb-4">
        <Row gutter={16} align="middle" className="mb-4">
          <Col span={6}>
            <Select
              value={reportParams.type}
              onChange={type => setReportParams(p => ({ ...p, type }))}
              style={{ width: '100%' }}
            >
              <Option value="monthly">Monthly</Option>
              <Option value="weekly">Weekly</Option>
              <Option value="quarterly">Quarterly</Option>
              <Option value="yearly">Yearly</Option>
              <Option value="custom">Custom Range</Option>
            </Select>
          </Col>

          {reportParams.type === 'yearly' && (
  <Col span={6}>
    <DatePicker
      picker="year"
      value={reportParams.year ? moment(reportParams.year, 'YYYY') : null}
      onChange={date => setReportParams(p => ({ 
        ...p, 
        year: date ? date.year() : null 
      }))}
    />
  </Col>
)}


          {reportParams.type === 'quarterly' && (
            <Col span={6}>
              <Select
                value={reportParams.quarter}
                onChange={quarter => setReportParams(p => ({ ...p, quarter }))}
                style={{ width: '100%' }}
              >
                {[1, 2, 3, 4].map(q => (
                  <Option key={q} value={q}>Q{q}</Option>
                ))}
              </Select>
            </Col>
          )}

{reportParams.type === 'monthly' && (
  <Col span={6}>
    <DatePicker
      picker="month"
      value={reportParams.year && reportParams.month 
        ? moment(`${reportParams.year}-${reportParams.month}`, 'YYYY-M') 
        : null}
      onChange={date => setReportParams(p => ({
        ...p,
        month: date ? date.month() + 1 : null, // Convert 0-indexed month to 1-based
        year: date ? date.year() : null
      }))}
    />
  </Col>
)}


          {reportParams.type === 'weekly' && (
            <Col span={6}>
              <DatePicker
                picker="week"
                value={reportParams.selected_week}
                onChange={date => setReportParams(p => ({ ...p, selected_week: date }))}
              />
            </Col>
          )}

          {reportParams.type === 'custom' && (
            <Col span={6}>
              <RangePicker
                value={reportParams.custom_range}
                onChange={dates => setReportParams(p => ({ ...p, custom_range: dates }))}
              />
            </Col>
          )}

          <Col>
            <Button 
              type="primary" 
              onClick={handleGenerateReport}
              icon={<FilePdfOutlined />}
              loading={generatingReport}
            >
              Generate
            </Button>
          </Col>
        </Row>

        <Row gutter={16} align="middle">
          <Col span={6}>
            <Select
              placeholder="Filter by Reference Number"
              value={reportParams.reference_number}
              onChange={value => setReportParams(p => ({ ...p, reference_number: value }))}
              allowClear
              style={{ width: '100%' }}
            >
              {referenceNumbers.map(ref => (
                <Option key={ref} value={ref}>{ref}</Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Space>
              <Button onClick={handleExportCSV} icon={<FileExcelOutlined />}>
                Export CSV
              </Button>
              <Button onClick={handleExportPDF} icon={<FilePdfOutlined />}>
                Export PDF
              </Button>
            </Space>
          </Col>
        </Row>

        {reportData.length > 0 && (
          <div className="mt-4">
            <Space size="large">
              <Statistic
                title="Total Entries"
                value={reportData.length}
                precision={0}
              />
              <Statistic
                title="Total Items Received"
                value={reportData.reduce((sum, entry) => sum + entry.items.length, 0)}
                precision={0}
              />
              <Statistic
                title="Total Inventory Value"
                value={reportData.reduce((sum, entry) => sum + 
                  entry.items.reduce((s, i) => s + (i.qty * i.unit_price), 0), 0)
                  .toLocaleString('en-US', CURRENCY_FORMAT)}
              />
            </Space>

            <Table
              columns={reportColumns}
              dataSource={reportData}
              rowKey="id"
              className="mt-4"
              pagination={{ pageSize: 10 }}
              scroll={{ x: true }}
            />
          </div>
        )}
      </Card>

      <Modal
        title={`${isEditing ? 'Edit' : 'Create'} Receiving Note`}
        visible={visible}
        onCancel={handleModalClose}
        footer={null}
        width={1000}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <div className="p-4">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Reference Number"
                  name="re_note"
                  rules={[{ required: true, message: 'Please enter reference number' }]}
                >
                  <Input placeholder="GRN-001" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Date"
                  name="date"
                  rules={[{ required: true, message: 'Please select date' }]}
                >
                  <DatePicker format={DATE_FORMAT} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  label="Received From"
                  name="received_from"
                  rules={[{ required: true, message: 'Please enter received from' }]}
                >
                  <Input placeholder="Supplier name" />
                </Form.Item>
              </Col>
            </Row>

            <Form.List name="items">
              {(fields, { add, remove }) => (
                <Card title="Items" className="mb-4">
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={16} key={key} align="top">
                      <Col span={2}>
                        <Form.Item
                          {...restField}
                          name={[name, 'sn']}
                          label="SN"
                          rules={[{ required: true }]}
                        >
                          <InputNumber 
                            min={1} 
                            style={{ width: '100%' }} 
                            readOnly 
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          {...restField}
                          name={[name, 'description']}
                          label="Description"
                          rules={[{ required: true }]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={3}>
                        <Form.Item
                          {...restField}
                          name={[name, 'order_no']}
                          label="Order No"
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={2}>
                        <Form.Item
                          {...restField}
                          name={[name, 'um']}
                          label="UM"
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={3}>
                        <Form.Item
                          {...restField}
                          name={[name, 'qty']}
                          label="QTY"
                          rules={[{ required: true }]}
                        >
                          <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={3}>
                        <Form.Item
                          {...restField}
                          name={[name, 'unit_price']}
                          label="Unit Price"
                          rules={[{ required: true }]}
                        >
                          <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={3}>
                        <Form.Item
                          {...restField}
                          name={[name, 'remark']}
                          label="Remark"
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={2}>
                        <Button
                          type="danger"
                          icon={<MinusOutlined />}
                          onClick={() => remove(name)}
                          style={{ marginTop: 30 }}
                        />
                      </Col>
                    </Row>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => {
                      const nextSN = fields.length + 1;
                      add({
                        sn: nextSN,
                        description: "",
                        order_no: "",
                        um: "",
                        qty: "",
                        unit_price: "",
                        remark: ""
                      });
                    }}
                    block
                    icon={<PlusOutlined />}
                  >
                    Add Item
                  </Button>
                </Card>
              )}
            </Form.List>

            <Row justify="end" gutter={16}>
              <Col>
                <Button onClick={handleModalClose}>Cancel</Button>
              </Col>
              <Col>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                  {isEditing ? 'Update' : 'Submit'}
                </Button>
              </Col>
            </Row>
          </div>
        </Form>
      </Modal>

      <Card>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={entries}
            rowKey="id"
            expandable={expandable}
            bordered
            scroll={{ x: true }}
            pagination={{ pageSize: 10, showSizeChanger: true }}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default FinishedGoodsReceivingNote;